// Small shared helper for admin auth state. Persists the JWT in localStorage
// and offers a wrapper that attaches the Authorization header to axios calls.
//
// We deliberately stash the token in localStorage rather than an http-only
// cookie so the SPA can read it back , the admin UI is JWT-based by design,
// and CSRF isn't applicable to bearer tokens.

import axios from "axios";

const STORAGE_KEY = "meditalk:admin-token:v1";

export function getAdminToken() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function setAdminToken(token) {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // ignore (private mode, quota exceeded, etc.)
  }
}

export function clearAdminToken() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function adminAuthHeader() {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Tiny axios helper for admin requests. Attaches the Bearer token, and on
 * 401 clears the token + redirects to /admin/login so a stale session never
 * silently fails.
 */
export const adminAxios = axios.create();

adminAxios.interceptors.request.use((config) => {
  const t = getAdminToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

adminAxios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearAdminToken();
      // Avoid bouncing back to login when we're already there.
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      if (
        !path.startsWith("/admin/login") &&
        !path.startsWith("/admin/accept-invite")
      ) {
        window.location.assign("/admin/login");
      }
    }
    return Promise.reject(err);
  },
);
